import React, { useState } from "react";
import { Upload, Link as LinkIcon, AlertCircle, Sparkles } from "lucide-react";
import { api } from "./api_service/api";
import VideoResultModal from "./VideoResultModal";

type Tab = "upload" | "url";

const VideoDemo: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>("upload");
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoUrl, setVideoUrl] = useState<string>("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);

  const handleAnalyze = async () => {
    if (isAnalyzing) return;
    setError(null);
    setResult(null);
    setIsAnalyzing(true);

    try {
      await api.login();

      let data: any;
      if (activeTab === "url") {
        if (!videoUrl.trim()) throw new Error("Please enter a video URL");
        data = await api.analyzeVideoUrl(videoUrl.trim());
      } else {
        if (!videoFile) throw new Error("Please select a video file");
        data = await api.analyzeVideoFile(videoFile);
      }

      console.log("Video analysis result →", data);
      setResult(data);
    } catch (err: any) {
      const msg = err instanceof Error ? err.message : "Analysis failed";
      console.error("Video analysis error:", err);
      setError(msg);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("video/")) {
      setVideoFile(file);
      setError(null);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 p-4 sm:p-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Tabs */}
            <div className="flex border-b border-slate-200">
              <button
                onClick={() => setActiveTab("upload")}
                className={`flex-1 py-4 px-6 font-semibold flex items-center justify-center gap-2 transition-colors ${
                  activeTab === "upload"
                    ? "bg-indigo-600 text-white"
                    : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                <Upload size={20} /> Upload File
              </button>
              <button
                onClick={() => setActiveTab("url")}
                className={`flex-1 py-4 px-6 font-semibold flex items-center justify-center gap-2 transition-colors ${
                  activeTab === "url"
                    ? "bg-indigo-600 text-white"
                    : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                <LinkIcon size={20} /> Paste URL
              </button>
            </div>

            <div className="p-8 min-h-96">
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-800">
                  <AlertCircle size={20} />
                  <span>{error}</span>
                </div>
              )}

              {!isAnalyzing && (
                <>
                  {activeTab === "upload" && (
                    <div className="space-y-6">
                      <div className="border-2 border-dashed border-indigo-300 rounded-xl p-12 text-center hover:border-indigo-500 transition cursor-pointer group">
                        <input
                          type="file"
                          accept="video/*"
                          onChange={handleFileChange}
                          className="hidden"
                          id="video-upload"
                        />
                        <label
                          htmlFor="video-upload"
                          className="cursor-pointer"
                        >
                          <Upload
                            size={56}
                            className="mx-auto text-indigo-500 group-hover:text-indigo-600"
                          />
                          <p className="mt-4 text-lg font-medium text-indigo-900">
                            Click to upload or drag & drop
                          </p>
                          <p className="text-sm text-gray-600">
                            MP4, WebM, MOV up to 500MB
                          </p>
                        </label>
                      </div>

                      {videoFile && (
                        <div className="bg-green-50 border border-green-300 rounded-lg p-4 text-green-800">
                          Selected: <strong>{videoFile.name}</strong> (
                          {(videoFile.size / 1024 / 1024).toFixed(1)} MB)
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === "url" && (
                    <div className="space-y-4">
                      <input
                        type="url"
                        placeholder="https://example.com/video.mp4"
                        value={videoUrl}
                        onChange={(e) => setVideoUrl(e.target.value)}
                        className="w-full text-indigo-600 px-4 py-3 border border-indigo-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                      />
                      <p className="text-sm text-gray-600">
                        Supports YouTube, Vimeo, Google Drive (public), direct
                        links
                      </p>
                    </div>
                  )}
                </>
              )}

              {isAnalyzing && (
                <div className="flex flex-col items-center justify-center py-32 space-y-10">
                  <div className="relative">
                    <div className="absolute inset-0 rounded-full bg-indigo-500 opacity-20 animate-ping"></div>
                    <div className="absolute inset-0 rounded-full bg-indigo-500 opacity-40 animate-ping delay-500"></div>
                    <div className="relative w-40 h-40">
                      <Sparkles
                        size={100}
                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-indigo-600 animate-pulse"
                      />
                      <Sparkles
                        size={40}
                        className="absolute top-2 left-1/2 -translate-x-1/2 text-purple-500 animate-spin"
                        style={{ animation: "spin 3s linear infinite" }}
                      />
                      <Sparkles
                        size={32}
                        className="absolute bottom-4 right-4 text-pink-500 animate-bounce"
                      />
                    </div>
                  </div>

                  <div className="text-center max-w-md">
                    <h2 className="text-4xl font-bold text-indigo-900 mb-4">
                      Analyzing Your Video…
                    </h2>
                    <p className="text-lg text-indigo-700">
                      Extracting frames and running deep AI detection across the
                      entire clip
                    </p>
                  </div>

                  <div className="w-full max-w-sm bg-indigo-100 rounded-full h-4 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-indigo-600 via-purple-500 to-pink-500 rounded-full"
                      style={{
                        width: "100%",
                        animation:
                          "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="px-8 pb-8">
              <div className="pt-6 border-t border-gray-200">
                <button
                  onClick={handleAnalyze}
                  disabled={
                    isAnalyzing ||
                    (activeTab === "upload" && !videoFile) ||
                    (activeTab === "url" && !videoUrl.trim())
                  }
                  className={`w-full flex items-center justify-center gap-3 py-4 rounded-xl font-bold text-white transition ${
                    isAnalyzing
                      ? "bg-indigo-400 cursor-wait"
                      : "bg-indigo-600 hover:bg-indigo-700 shadow-lg hover:shadow-xl"
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isAnalyzing ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Analyzing Video...
                    </>
                  ) : (
                    <>
                      <Sparkles size={20} />
                      Analyze Video
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="bg-indigo-50 border-t border-indigo-200 p-6 text-sm text-indigo-700">
              <div className="flex gap-3">
                <AlertCircle
                  size={20}
                  className="text-indigo-600 flex-shrink-0"
                />
                <div>
                  <p className="font-semibold text-indigo-900">
                    Supported Sources
                  </p>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li>Direct video files (MP4, WebM, MOV)</li>
                    <li>YouTube, Vimeo, TikTok</li>
                    <li>Google Drive (publicly shared)</li>
                    <li>Any public video URL</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {result && (
        <VideoResultModal result={result} onClose={() => setResult(null)} />
      )}
    </>
  );
};

export default VideoDemo;
